import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Eye, EyeOff, UserPlus, Loader2, ArrowLeft, Phone, Mail, MessageCircle, MapPin, Building2, Clock, Hospital, Stethoscope } from "lucide-react";

const LOGO_URL = "/logo_cmes.jpg";

const COMMUNES = [
  "Abobo", "Adjamé", "Attécoubé", "Cocody", "Koumassi", 
  "Marcory", "Plateau", "Port-Bouët", "Treichville", "Yopougon",
  "Bingerville", "Songon", "Anyama", "Grand-Bassam", "Bouaké",
  "Yamoussoukro", "San-Pédro", "Daloa", "Korhogo", "Man",
  "Autres"
];

const VILLES = [
  "Abidjan", "Bouaké", "Yamoussoukro", "San-Pédro", "Daloa", 
  "Korhogo", "Man", "Gagnoa", "Divo", "Abengourou",
  "Autres"
];

const ESTABLISHMENT_TYPES = [
  { value: "hopital_public", label: "Hôpital public" },
  { value: "hopital_prive", label: "Hôpital privé / Clinique" },
  { value: "centre_sante", label: "Centre de santé / Dispensaire" },
  { value: "laboratoire", label: "Laboratoire d'analyses" },
  { value: "cabinet_medical", label: "Cabinet médical" },
  { value: "maternite", label: "Maternité" },
  { value: "centre_imagerie", label: "Centre d'imagerie médicale" },
  { value: "autre", label: "Autre établissement de santé" },
];

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationPending, setRegistrationPending] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    pharmacy_name: "",
    commune: "",
    ville: "",
    custom_ville: "",
    custom_commune: "",
    phone: "",
    whatsapp: "",
    role: "pharmacy",
    establishment_type: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        ville: formData.ville === "Autres" ? formData.custom_ville : formData.ville,
        commune: formData.commune === "Autres" ? formData.custom_commune : formData.commune,
      };
      delete submitData.custom_ville;
      delete submitData.custom_commune;
      if (formData.role !== "etablissement") delete submitData.establishment_type;
      const result = await register(submitData);
      if (result.status === "pending") {
        setRegistrationPending(true);
        toast.success("Demande soumise avec succès !");
      } else {
        toast.success(`Bienvenue sur CMES INTEL-STOCK, ${result.full_name}!`);
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  if (registrationPending) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 md:p-8" style={{background: '#F9F8F6', fontFamily: "'Manrope', sans-serif"}}>
        <div className="w-full max-w-lg">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="CMES" className="h-20 w-auto mb-4" />
            <h1 className="text-3xl font-outfit font-bold">
              <span className="text-cyan-600">CMES</span> <span className="text-amber-500">CMES INTEL-STOCK</span>
            </h1>
          </div>
          <Card className="bg-white/90 backdrop-blur-xl border-cyan-100 shadow-xl" data-testid="pending-registration-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-outfit font-bold text-slate-800 mb-2">Demande soumise avec succès</h2>
              <p className="text-slate-500 mb-6">
                Votre demande de création de compte a été envoyée à l'administrateur pour validation. 
                Vous recevrez une notification une fois votre compte activé.
              </p>
              <div className="bg-cyan-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-cyan-700">
                  <strong>Prochaine étape :</strong> L'administrateur CMES examinera votre demande et activera votre compte. 
                  Vous pourrez alors vous connecter avec vos identifiants.
                </p>
              </div>
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8" style={{background: '#F9F8F6', fontFamily: "'Manrope', sans-serif"}}>
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="CMES" className="h-20 w-auto mb-4" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <span style={{ color: "#025946" }}>CMES</span> <span style={{ color: "#C9A25E" }}>Pharma Intel</span>
          </h1>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold mt-2" style={{ color: "#025946" }}>─── Trois services, une seule vision</p>
        </div>

        <Card className="bg-white shadow-xl rounded-2xl border" style={{ borderColor: "#E8E4DA" }}>
          <CardHeader className="space-y-1 border-b pb-6" style={{ borderColor: "#E8E4DA" }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: "rgba(2,89,70,0.10)" }}>
                <Building2 className="w-6 h-6" style={{ color: "#025946" }} />
              </div>
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[0.3em]" style={{ color: "#025946" }}>─── Espace pharmacien</p>
                <CardTitle className="text-3xl pt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#0F172A", fontWeight: 400, letterSpacing: "-0.01em" }}>Cr&eacute;er un compte</CardTitle>
                <CardDescription className="text-slate-500 text-sm">
                  Pharmacie, &Eacute;tablissement de sant&eacute; ou Agent Commercial — rejoignez le r&eacute;seau CMES
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role selector */}
              <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                <button type="button" onClick={() => setFormData(p => ({ ...p, role: "pharmacy", establishment_type: "" }))} data-testid="role-pharmacy"
                  className={`flex-1 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${formData.role === "pharmacy" ? "bg-cyan-600 text-white shadow-md" : "text-slate-600 hover:bg-white"}`}>
                  <Building2 className="w-4 h-4" /> Pharmacie
                </button>
                <button type="button" onClick={() => setFormData(p => ({ ...p, role: "etablissement", establishment_type: "" }))} data-testid="role-etablissement"
                  className={`flex-1 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${formData.role === "etablissement" ? "bg-emerald-600 text-white shadow-md" : "text-slate-600 hover:bg-white"}`}>
                  <Hospital className="w-4 h-4" /> Établissement
                </button>
                <button type="button" onClick={() => setFormData(p => ({ ...p, role: "agent commercial", establishment_type: "" }))} data-testid="role-agent commercial"
                  className={`flex-1 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${formData.role === "agent commercial" ? "bg-indigo-600 text-white shadow-md" : "text-slate-600 hover:bg-white"}`}>
                  <UserPlus className="w-4 h-4" /> Agent Commercial
                </button>
              </div>
              {formData.role === "agent commercial" && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-700">
                  Votre compte sera soumis à validation par l'administrateur CMES avant activation.
                </div>
              )}
              {formData.role === "etablissement" && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
                  Accès complet aux 3 services CMES : LOG (logistique), INTEL-STOCK (stocks) et TECH-CONSEIL (IA pharmaceutique). Votre compte sera validé par l'administrateur.
                </div>
              )}

              {/* Section Identite */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-cyan-700 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-600 text-xs">1</span>
                  Identité
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-slate-600">
                      {formData.role === "etablissement" ? "Nom du responsable / Directeur" : "Nom complet du responsable"}
                    </Label>
                    <Input
                      id="full_name"
                      type="text"
                      placeholder={formData.role === "etablissement" ? "Dr. Koné Marie" : "Dr. Kouassi Jean"}
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      data-testid="register-fullname-input"
                      className="bg-white border-slate-200 focus:border-cyan-400 text-slate-800 placeholder:text-slate-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pharmacy_name" className="text-slate-600">
                      {formData.role === "agent commercial" ? "Société / Indépendant" : formData.role === "etablissement" ? "Nom de l'établissement" : "Nom de la Pharmacie"}
                    </Label>
                    <Input
                      id="pharmacy_name"
                      type="text"
                      placeholder={formData.role === "agent commercial" ? "CMES Transport (optionnel)" : formData.role === "etablissement" ? "CHU de Cocody" : "Pharmacie du Plateau"}
                      value={formData.pharmacy_name}
                      onChange={(e) => setFormData({ ...formData, pharmacy_name: e.target.value })}
                      required={formData.role !== "agent commercial"}
                      data-testid="register-pharmacy-input"
                      className="bg-white border-slate-200 focus:border-cyan-400 text-slate-800 placeholder:text-slate-400"
                    />
                  </div>

                  {formData.role === "etablissement" && (
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="establishment_type" className="text-slate-600 flex items-center gap-2">
                        <Stethoscope className="w-4 h-4 text-emerald-500" />
                        Type d'établissement
                      </Label>
                      <Select
                        value={formData.establishment_type}
                        onValueChange={(value) => setFormData({ ...formData, establishment_type: value })}
                      >
                        <SelectTrigger
                          data-testid="register-establishment-type"
                          className="bg-white border-slate-200 focus:border-emerald-400 text-slate-800"
                        >
                          <SelectValue placeholder="Sélectionner le type d'établissement..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          {ESTABLISHMENT_TYPES.map((t) => (
                            <SelectItem
                              key={t.value}
                              value={t.value}
                              className="text-slate-700 focus:bg-emerald-50 focus:text-emerald-700"
                            >
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Section Localisation */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-cyan-700 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-600 text-xs">2</span>
                  Localisation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ville" className="text-slate-600 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-cyan-500" />
                      Ville
                    </Label>
                    <Select
                      value={formData.ville}
                      onValueChange={(value) => setFormData({ ...formData, ville: value, custom_ville: "" })}
                    >
                      <SelectTrigger 
                        data-testid="register-ville-select"
                        className="bg-white border-slate-200 focus:border-cyan-400 text-slate-800"
                      >
                        <SelectValue placeholder="Sélectionner la ville..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        {VILLES.map((ville) => (
                          <SelectItem 
                            key={ville} 
                            value={ville}
                            className="text-slate-700 focus:bg-cyan-50 focus:text-cyan-700"
                          >
                            {ville === "Autres" ? "Autres (a preciser)" : ville}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.ville === "Autres" && (
                      <Input
                        type="text"
                        placeholder="Nom de votre ville..."
                        value={formData.custom_ville}
                        onChange={(e) => setFormData({ ...formData, custom_ville: e.target.value })}
                        required
                        data-testid="register-custom-ville-input"
                        className="mt-2 bg-white border-slate-200 focus:border-cyan-400 text-slate-800 placeholder:text-slate-400"
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="commune" className="text-slate-600 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-500" />
                      Commune
                    </Label>
                    <Select
                      value={formData.commune}
                      onValueChange={(value) => setFormData({ ...formData, commune: value, custom_commune: "" })}
                    >
                      <SelectTrigger 
                        data-testid="register-commune-select"
                        className="bg-white border-slate-200 focus:border-cyan-400 text-slate-800"
                      >
                        <SelectValue placeholder="Sélectionner la commune..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        {COMMUNES.map((commune) => (
                          <SelectItem 
                            key={commune} 
                            value={commune}
                            className="text-slate-700 focus:bg-cyan-50 focus:text-cyan-700"
                          >
                            {commune === "Autres" ? "Autres (a preciser)" : commune}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.commune === "Autres" && (
                      <Input
                        type="text"
                        placeholder="Nom de votre commune..."
                        value={formData.custom_commune}
                        onChange={(e) => setFormData({ ...formData, custom_commune: e.target.value })}
                        required
                        data-testid="register-custom-commune-input"
                        className="mt-2 bg-white border-slate-200 focus:border-cyan-400 text-slate-800 placeholder:text-slate-400"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Section Contact */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-cyan-700 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-600 text-xs">3</span>
                  Coordonnées de contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-600 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-cyan-500" />
                      Email professionnel
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="contact@pharmacie.ci"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                      className="bg-white border-slate-200 focus:border-cyan-400 text-slate-800 placeholder:text-slate-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-600 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-cyan-500" />
                      Téléphone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+225 07 XX XX XX XX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="register-phone-input"
                      className="bg-white border-slate-200 focus:border-cyan-400 text-slate-800 placeholder:text-slate-400"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="whatsapp" className="text-slate-600 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-green-500" />
                      WhatsApp (pour notifications urgentes)
                    </Label>
                    <Input
                      id="whatsapp"
                      type="tel"
                      placeholder="+225 07 XX XX XX XX"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                      data-testid="register-whatsapp-input"
                      className="bg-white border-slate-200 focus:border-green-400 text-slate-800 placeholder:text-slate-400"
                    />
                    <p className="text-xs text-slate-400">Recevez les alertes de destruction et confirmations par WhatsApp</p>
                  </div>
                </div>
              </div>

              {/* Section Sécurité */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-cyan-700 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-600 text-xs">4</span>
                  Sécurité
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-600">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Minimum 8 caractères"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="register-password-input"
                      className="bg-white border-slate-200 focus:border-cyan-400 text-slate-800 placeholder:text-slate-400 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit-btn"
                className="w-full text-white font-bold py-6 text-sm rounded-full shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5"
                style={{ background: "#025946" }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Cr&eacute;er mon compte
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                data-testid="login-link"
                className="text-slate-500 hover:text-emerald-700 inline-flex items-center gap-2 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour &agrave; la connexion
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-slate-400 text-sm mt-6">
          © 2024 CMES - Coaching Médical et Épanouissement Social
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
